import React, { useState, useEffect, useRef } from "react";
import { Bot, Check, ChevronsUpDown, Plus } from "lucide-react";

// `allowAll` exposes the "All Agents" entry. Screens that filter a list want it;
// screens that act on one agent (e.g. scenario generation) must pass false.
const AgentPicker = ({
  agents,
  selectedAgent,
  setSelectedAgent,
  onAdd,
  className = "",
  allowAll = true,
  placeholder,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-muted/50 hover:bg-muted transition-colors text-sm"
      >
        <Bot className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={2} />
        <span className="text-foreground font-medium max-w-[200px] truncate">
          {selectedAgent ? selectedAgent.name : placeholder || (allowAll ? "All Agents" : "Select an agent")}
        </span>
        <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={2} />
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1 w-64 bg-card rounded-lg border border-border shadow-lg z-50 py-1 max-h-72 overflow-auto">
          {allowAll && (
            <>
              <button
                onClick={() => { setSelectedAgent(null); setOpen(false); }}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted/50 transition-colors ${
                  !selectedAgent ? "text-indigo-600 font-medium" : "text-foreground"
                }`}
              >
                {!selectedAgent ? <Check className="h-3.5 w-3.5" strokeWidth={2.5} /> : <span className="w-3.5" />}
                <span>All Agents</span>
              </button>
              <div className="h-px bg-muted my-1" />
            </>
          )}
          {agents.map((a) => (
            <button
              key={a.id}
              onClick={() => { setSelectedAgent(a); setOpen(false); }}
              className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted/50 transition-colors ${
                selectedAgent?.id === a.id ? "text-indigo-600 font-medium" : "text-foreground"
              }`}
            >
              {selectedAgent?.id === a.id ? <Check className="h-3.5 w-3.5" strokeWidth={2.5} /> : <span className="w-3.5" />}
              <span className="truncate">{a.name}</span>
              <span className="text-[10px] text-muted-foreground font-mono ml-auto">#{a.id}</span>
            </button>
          ))}
          {onAdd && (
            <>
              <div className="h-px bg-muted my-1" />
              <button
                onClick={() => { onAdd(); setOpen(false); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-indigo-600 hover:bg-indigo-50 transition-colors"
              >
                <Plus className="h-3.5 w-3.5" strokeWidth={2} />
                <span>Add Agent</span>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AgentPicker;
